import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, EnvelopeSimple, Phone, IdentificationBadge, Buildings, Briefcase, CalendarCheck, CurrencyCircleDollar, Camera } from "@phosphor-icons/react";

const ROLE_LABEL = { admin: "Administrator", manager: "Manager", employee: "Employee" };

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const pesoFmt = (v) => `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const photoSrc = user?.picture
    ? (user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`)
    : null;

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const items = [
    { icon: EnvelopeSimple, label: "Email", value: user?.email },
    { icon: Phone, label: "Phone", value: user?.phone },
    { icon: IdentificationBadge, label: "Employee ID", value: user?.employee_id },
    { icon: Buildings, label: "Department", value: user?.department },
    { icon: Briefcase, label: "Position", value: user?.position },
    { icon: CalendarCheck, label: "Vacation Leave credits", value: stats ? String(stats.my_vl_credits ?? 0) : null, testid: "profile-vl-credits" },
    { icon: CalendarCheck, label: "Sick Leave credits", value: stats ? String(stats.my_sl_credits ?? 0) : null, testid: "profile-sl-credits" },
    { icon: CalendarCheck, label: "Other credits", value: stats ? String(stats.my_others_credits ?? 0) : null, testid: "profile-others-credits" },
    { icon: CurrencyCircleDollar, label: "Loan balance", value: stats ? pesoFmt(stats.my_loan_balance) : null, testid: "profile-loan-balance" },
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Account</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">My Profile</h1>
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="bg-primary p-8 flex items-center gap-5">
          <div className="relative h-20 w-20 shrink-0">
            <div className="h-20 w-20 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-2xl font-heading font-bold text-white overflow-hidden">
              {photoSrc ? <img src={photoSrc} alt="" className="h-20 w-20 rounded-full object-cover" /> : initials}
            </div>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="change-photo-button" aria-label="Change profile photo"
              className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-white text-primary border border-border shadow flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-60">
              <Camera weight="bold" className="h-4 w-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              data-testid="photo-input" onChange={onPhoto} />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-white">{user?.name}</h2>
            <Badge className="mt-2 bg-white/15 text-white border-white/20 hover:bg-white/20">{ROLE_LABEL[user?.role]}</Badge>
          </div>
        </div>
        <div className="p-6 divide-y divide-border">
          {items.map((it) => (
            <div key={it.label} className="flex items-center gap-4 py-3.5" data-testid={it.testid}>
              <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <it.icon weight="duotone" className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">{it.label}</p>
                <p className={`text-sm font-medium truncate ${String(it.value || "").trim().startsWith("-") ? "text-destructive" : ""}`}>{it.value || "Not set"}{String(it.value || "").trim().startsWith("-") ? " (overdrawn)" : ""}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-border shadow-none flex items-start gap-4">
        <ShieldCheck weight="duotone" className="h-6 w-6 text-primary shrink-0" />
        <div>
          <h3 className="font-heading font-bold">Access & permissions</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {user?.role === "admin"
              ? "As an administrator you can manage the employee directory, view confidential salary and document files, and approve leave requests."
              : user?.role === "manager"
              ? "As a manager you can browse the employee directory and approve or reject leave requests. Confidential files remain admin-only."
              : "As an employee you can browse the directory and submit leave requests. Confidential files are restricted to administrators."}
          </p>
        </div>
      </Card>
    </div>
  );
}
